import React from 'react';
import ReactSelect, { components } from 'react-select';

import type { Props, GroupBase, DropdownIndicatorProps, OptionProps, ControlProps } from 'react-select';

import { borderRadius } from '../../../theme/border';
import { rebrand } from '../../../theme/color';
import { spacing } from '../../../theme/spacing';
import { ArrowDown } from '../atoms/icons/ArrowDown';
import { CheckCircle } from '../atoms/icons/CheckCircle';
import { style } from 'typestyle';
import { center, flexRoot } from 'csstips';
import { transitionQuickEase } from '../../../theme/transition';

const styles = {
  optionInner: style(flexRoot, center, {
    gap: spacing.XSMALL,
  }),
  selectedIcon: style({
    marginLeft: 'auto',
  }),
  dropdownIcon: (isOpen: boolean) =>
    style({
      transform: isOpen ? 'rotate(180deg)' : 'none',
    }),
};

const Control = ({ children, ...props }: ControlProps) => {
  return (
    <components.Control
      {...props}
      innerProps={Object.assign({}, props.innerProps, { 'data-test-styled-select': props.selectProps.inputId || '' })}
    >
      {children}
    </components.Control>
  );
};

const Option = ({ children, ...props }: OptionProps) => {
  return (
    <components.Option {...props}>
      <div className={styles.optionInner}>
        {children}
        {props.isSelected && (
          <CheckCircle
            className={styles.selectedIcon}
            color={rebrand.contrast[50].toString()}
            checkColor={rebrand.neutralOnDark[20].toString()}
          />
        )}
      </div>
    </components.Option>
  );
};

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <ArrowDown
        color={rebrand.contrast[100].toString()}
        className={styles.dropdownIcon(props.selectProps.menuIsOpen)}
      />
    </components.DropdownIndicator>
  );
};

/**
 * A fully accessible select built using the react-select library
 */
export const StyledSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: Props<Option, IsMulti, Group>
): React.ReactElement => {
  return (
    <ReactSelect
      {...props}
      components={{ Control, Option, DropdownIndicator, IndicatorSeparator: null }}
      styles={{
        container: (baseStyles) => ({
          ...baseStyles,
        }),
        control: (baseStyles, { isFocused, selectProps: { menuIsOpen } }) => ({
          ...baseStyles,
          width: '100%',
          borderRadius: borderRadius.SMALL,
          borderColor: rebrand.neutralOnDark[20].toString(),
          backgroundColor: menuIsOpen ? rebrand.neutralOnDark[40].toString() : rebrand.neutralOnDark[20].toString(),
          fontFamily: 'inherit',
          fontWeight: 400,
          boxShadow: 'none',
          outline: isFocused && !menuIsOpen ? `2px solid ${rebrand.primary[100].toString()}` : 'none',
          outlineOffset: '1px',
          transition: `background-color ${transitionQuickEase}`,
          '&:hover': {
            backgroundColor: menuIsOpen ? rebrand.neutralOnDark[40].toString() : rebrand.neutralOnDark[30].toString(),
            cursor: 'pointer',
          },
        }),
        input: (baseStyles) => ({
          ...baseStyles,
        }),
        menuList: (baseStyles) => ({
          ...baseStyles,
          padding: spacing.XSMALL,
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          backgroundColor: rebrand.neutralOnDark[10].toString(),
          boxShadow: 'none',
          fontWeight: 400,
          borderRadius: borderRadius.MEDIUM,
          marginTop: spacing.XXSMALL,
        }),
        option: (baseStyles, { isFocused, isSelected, isDisabled }) => ({
          ...baseStyles,
          backgroundColor: isSelected || isFocused ? rebrand.neutralOnDark[20].toString() : 'transparent',
          borderRadius: borderRadius.SMALL,
          padding: spacing.XSMALL,
          color: 'inherit',
          ':active': {
            backgroundColor: !isDisabled && isSelected ? rebrand.neutralOnDark[20].toString() : undefined,
          },
          '&:hover': {
            cursor: 'pointer',
          },
        }),
      }}
      blurInputOnSelect={true}
      isSearchable={false}
    />
  );
};
